import type React from "react"
import { useId, useState } from "react"
import { useSearchParams } from "react-router"
import { useCmApi } from "@/shared/hooks/useCmApi"
import styles from "./AddPermissionPage.module.css"

const AddPermissionPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [resource, setResource] = useState(() => searchParams.get("resource") || "")
  const [subject, setSubject] = useState(() => searchParams.get("subject") || "")
  const [action, setAction] = useState(() => searchParams.get("action") || "")
  const scope = searchParams.get("scope") || ""

  const cmApi = useCmApi()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [warning, setWarning] = useState<string | null>(null)
  const resourceId = useId()
  const actionId = useId()
  const subjectId = useId()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(false)
    setWarning(null)
    try {
      await cmApi.createNewPermission({ resource, subject, action })
      setSuccess(true)
    } catch (err: unknown) {
      if (typeof err === "object" && err !== null) {
        const status = (err as { status?: number }).status
        const message = (err as { detail?: string }).detail
        if (status === 401 || status === 403) {
          setError("You do not have enough access to add a new permission.")
        } else if (status === 409) {
          setWarning("A permission with these values already exists.")
        } else {
          setError(message || "Failed to add permission!")
        }
      } else {
        setError("Failed to add permission")
      }
    } finally {
      setLoading(false)
    }
  }

  const isDisabled = scope === "approval"

  return (
    <form onSubmit={handleSubmit} className={isDisabled ? `${styles.form} ${styles.disabledBg}` : styles.form}>
      <h2 className={styles.heading}>Add New Permission</h2>
      <div className={styles.container}>
        <div className={styles.inputGroup}>
          <label htmlFor={resourceId} className={styles.label}>
            Resource
          </label>
          <input
            id={resourceId}
            type="text"
            value={resource}
            onChange={(e) => setResource(e.target.value)}
            className={styles.input}
            disabled={isDisabled}
          />
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor={actionId} className={styles.label}>
            Action
          </label>
          <input
            id={actionId}
            type="text"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className={styles.input}
            disabled={isDisabled}
          />
        </div>
        <div className={styles.inputGroup}>
          <label htmlFor={subjectId} className={styles.label}>
            Subject
          </label>
          <input
            id={subjectId}
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={styles.input}
            disabled={isDisabled}
          />
        </div>
        <button type="submit" className={styles.button} disabled={loading}>
          {loading ? "Adding..." : "Add permission"}
        </button>
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>Permission added successfully!</div>}
        {warning && <div className={styles.warning}>{warning}</div>}
      </div>
    </form>
  )
}

export default AddPermissionPage
